import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { useCurrencyExchangeRates } from "@/hooks/use-currency-exchange-rates"
import i18n from "@/lib/i18n"
import { appTaskCurrencyFromCountryCode, isAppTaskCurrency, resolveUserCurrency } from "@/lib/app-currency"
import type { UsdPerUnitMap } from "@/lib/currency-convert"
import { messageFromUnknown } from "@/lib/errors"
import { provisionWorkspace, type ProvisionWorkspaceInput } from "@/lib/provision-workspace"
import { toastPartialWorkspaceSeed } from "@/lib/toast-partial-workspace-seed"
import { normalizeVisaTypeForDB, resolveCanonicalVisaTypeForProvision } from "@/lib/visa-utils"
import { useAuth } from "@/lib/auth"
import { useUpgradePrompt } from "@/providers/upgrade-prompt-context"
import { supabase } from "@/lib/supabase"
import { WorkspaceLimitError } from "@/lib/usage-limits"
import type { OnboardingDraft, ProfileRow } from "@/types/profile"

function toastProfileSaveError(t: (k: string, o?: Record<string, unknown>) => string, pError: { message: string }) {
  const msg = pError.message
  const isMissingColumn =
    /schema cache/i.test(msg) || /could not find.*column/i.test(msg)

  toast.error(t("onboarding.profileNotSaved", { error: msg }), {
    description: isMissingColumn
      ? t("onboarding.schemaHelp")
      : undefined,
    duration: isMissingColumn ? 14_000 : 6_000,
  })
}

export function useOnboardingProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setProfile(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const { data, error: qError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle()

      if (qError) throw qError
      setProfile((data ?? null) as ProfileRow | null)
    } catch (e) {
      const msg = messageFromUnknown(e) || "Could not load profile."
      setError(msg)
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /** False while loading, on profile fetch error, or when signed out — avoids treating RLS errors as “needs onboarding”. */
  const needsOnboarding =
    !loading &&
    !error &&
    Boolean(user) &&
    (profile === null || !profile.onboarding_completed_at)

  return { profile, loading, error, refresh, needsOnboarding }
}

function destinationFromDraft(draft: OnboardingDraft): string {
  const parts = [draft.country.trim(), draft.city.trim()].filter(Boolean)
  return parts.length > 0 ? parts.join(" · ") : i18n.t("workspaces.destinationTbd")
}

/** Maps onboarding draft to `provisionWorkspace` input (visa seeding uses `visa_metadata` only). */
export function buildProvisionWorkspaceInput(
  userId: string,
  draft: OnboardingDraft,
  usdPerUnit?: UsdPerUnitMap | null
): ProvisionWorkspaceInput {
  const natDraft = draft.nationality?.trim() ?? ""
  const destDraft = draft.destinationCode?.trim() ?? ""
  if (!natDraft || !destDraft) {
    throw new Error(
      `buildProvisionWorkspaceInput: nationality and destinationCode must be non-empty (nationality=${JSON.stringify(draft.nationality)}, destinationCode=${JSON.stringify(draft.destinationCode)})`
    )
  }

  const hasExistingVisa = draft.hasVisa === true || draft.hasExistingVisa === true
  const canonical = draft.canonicalVisaTypesForRoute
  const resolveInput = {
    canonicalVisaTypesForRoute: canonical,
    visaType: draft.visaType?.trim() ? normalizeVisaTypeForDB(draft.visaType.trim()) : draft.visaType,
    existingVisa: draft.existingVisa?.trim()
      ? normalizeVisaTypeForDB(draft.existingVisa.trim())
      : draft.existingVisa,
  }
  const resolved = resolveCanonicalVisaTypeForProvision(resolveInput)
  const pickedRaw = draft.existingVisa?.trim() || draft.visaType?.trim() || ""
  const picked = pickedRaw ? normalizeVisaTypeForDB(pickedRaw) : ""
  /**
   * When canonical route types exist, prefer resolver output; if it is empty (edge timing / mismatch),
   * fall back to the explicit draft pick so provisioning still queries `visa_metadata` with a real slug.
   */
  let visaType =
    canonical !== undefined && canonical.length > 0 ? resolved || picked : picked
  if (
    !visaType?.trim() &&
    draft.visaRouteFound === true &&
    draft.canonicalVisaTypesForRoute?.length === 1
  ) {
    const only = draft.canonicalVisaTypesForRoute[0]?.trim()
    if (only) {
      visaType = normalizeVisaTypeForDB(only)
    }
  }

  const residenceOrPassport = (draft.currentLocation?.trim() || draft.nationality.trim()).toUpperCase()

  return {
    userId,
    nationalityCode: draft.nationality,
    originCode: residenceOrPassport || undefined,
    destinationCode: draft.destinationCode,
    destination: destinationFromDraft(draft),
    visaType,
    hasExistingVisa,
    goal: draft.goal ?? undefined,
    budget: draft.budget,
    budgetCurrency:
      (isAppTaskCurrency(draft.budgetCurrency, usdPerUnit) && draft.budgetCurrency) ||
      appTaskCurrencyFromCountryCode(draft.currentLocation || draft.nationality, usdPerUnit),
    timeline: draft.timeline ?? undefined,
    visaStatus: draft.visaStatus,
    locale: typeof navigator !== "undefined" ? navigator.language : undefined,
  }
}

export type FinishOnboardingInput = {
  draft: OnboardingDraft
}

/**
 * Creates a workspace from onboarding data, seeds visa tasks from `visa_metadata` when applicable,
 * and default resources. Does not update `profiles` or `onboarding_profiles` — compose with
 * profile updates from `useFinishOnboarding` or dashboard flows.
 */
export async function provisionWorkspaceFromOnboardingDraft(
  userId: string,
  draft: OnboardingDraft,
  usdPerUnit?: UsdPerUnitMap | null
): Promise<{ workspaceId: string; error?: string }> {
  const result = await provisionWorkspace(buildProvisionWorkspaceInput(userId, draft, usdPerUnit), supabase)
  return { workspaceId: result.workspaceId, ...(result.error ? { error: result.error } : {}) }
}

/** Upsert partial onboarding data to `onboarding_profiles` after each step. */
export async function saveOnboardingStep(
  userId: string,
  draft: Partial<OnboardingDraft>
): Promise<void> {
  const payload = {
    profile_id: userId,
    ...(draft.goal !== undefined && { goal: draft.goal }),
    ...(draft.nationality !== undefined && { nationality: draft.nationality || null }),
    ...(draft.currentLocation !== undefined && { current_location: draft.currentLocation || null }),
    ...(draft.country !== undefined && { destination: draft.country || null }),
    ...(draft.destinationCode !== undefined && {
      destination_code: draft.destinationCode?.trim() || null,
    }),
    ...(draft.timeline !== undefined && { timeline: draft.timeline }),
    ...(draft.budget !== undefined && { budget: draft.budget ?? null }),
    ...(draft.budgetCurrency !== undefined && { budget_currency: draft.budgetCurrency || null }),
    /** Step 5 (Step5Visa): route visa type lives in `existingVisa`; never map `who` to `visa_type`. */
    ...(draft.existingVisa !== undefined && {
      existing_visa: draft.existingVisa,
      visa_type: draft.existingVisa,
    }),
    ...(draft.visaStatus !== undefined && { visa_status: draft.visaStatus }),
    ...(draft.hasVisa !== undefined && { has_visa: draft.hasVisa }),
    ...(draft.who !== undefined && { who: draft.who }),
    ...(draft.acceptedTerms !== undefined && { accepted_terms: draft.acceptedTerms }),
  }
  const { error } = await supabase
    .from("onboarding_profiles")
    .upsert(payload, { onConflict: "profile_id" })
  if (error) {
    toast.error(i18n.t("onboarding.failedToSave"))
    throw error
  }
}

export function useFinishOnboarding() {
  const { t: i18nextT } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { openUpgrade } = useUpgradePrompt()
  const { usdPerUnit } = useCurrencyExchangeRates()
  const [submitting, setSubmitting] = useState(false)
  const finishInFlightRef = useRef(false)

  const finishOnboarding = useCallback(
    async (input: FinishOnboardingInput): Promise<string> => {
      if (!user?.id) {
        toast.error(i18nextT("onboarding.mustSignIn"))
        throw new Error("Not authenticated.")
      }
      if (finishInFlightRef.current) {
        throw new Error("Onboarding already in progress.")
      }
      finishInFlightRef.current = true

      setSubmitting(true)
      try {
        const { draft } = input
        const completedAt = new Date().toISOString()

        const result = await provisionWorkspace(
          buildProvisionWorkspaceInput(user.id, draft, usdPerUnit),
          supabase
        )
        const { workspaceId } = result

        if (result.error) {
          toastPartialWorkspaceSeed(i18nextT, navigate, workspaceId, result.error)
        } else {
          toast.success(i18nextT("onboarding.workspaceCreated"))
        }

        const residence = draft.currentLocation.trim().toUpperCase()
        const cor =
          residence.length === 2
            ? residence
            : draft.nationality.trim().length === 2
              ? draft.nationality.trim().toUpperCase()
              : null
        const preferredCurrency =
          (isAppTaskCurrency(draft.budgetCurrency, usdPerUnit) && draft.budgetCurrency?.trim()) ||
          resolveUserCurrency(
            { preferred_currency: null, country_of_residence: cor },
            {
              navigatorLanguage:
                typeof navigator !== "undefined" ? navigator.language : undefined,
            }
          )

        const patch = {
          email: user.email ?? null,
          onboarding_goal: draft.goal,
          onboarding_country: draft.country.trim() || null,
          onboarding_city: draft.city.trim() || null,
          onboarding_completed_at: completedAt,
          country_of_residence: residence.length === 2 ? residence : null,
          preferred_currency: preferredCurrency,
        }

        const { data: existing, error: exErr } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", user.id)
          .maybeSingle()

        if (exErr) throw exErr

        const meta = user.user_metadata as { full_name?: string } | undefined
        if (existing?.id) {
          const { error: pError } = await supabase.from("profiles").update(patch).eq("id", user.id)
          if (pError) toastProfileSaveError(i18nextT, pError)
        } else {
          const { error: pError } = await supabase.from("profiles").insert({
            id: user.id,
            full_name: meta?.full_name?.trim() || "",
            plan: "free",
            ...patch,
          })
          if (pError) toastProfileSaveError(i18nextT, pError)
        }

        await saveOnboardingStep(user.id, draft)

        return workspaceId
      } catch (e) {
        if (e instanceof WorkspaceLimitError) {
          openUpgrade(i18nextT("upgrade.featureWorkspace"))
          throw e
        }
        const msg = messageFromUnknown(e) || i18nextT("onboarding.failedToComplete")
        toast.error(msg)
        throw e
      } finally {
        finishInFlightRef.current = false
        setSubmitting(false)
      }
    },
    [user, i18nextT, openUpgrade, usdPerUnit, navigate]
  )

  return { finishOnboarding, submitting }
}

/**
 * Marks onboarding as complete without creating a workspace.
 * Used when the user dismisses the onboarding flow early.
 */
export function useSkipOnboarding() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [skipping, setSkipping] = useState(false)

  const skipOnboarding = useCallback(async () => {
    if (!user?.id || skipping) return
    setSkipping(true)
    try {
      const completedAt = new Date().toISOString()
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle()

      if (existing?.id) {
        const { error } = await supabase
          .from("profiles")
          .update({ onboarding_completed_at: completedAt })
          .eq("id", user.id)
        if (error) {
          console.error(error)
          toast.error("Failed to update profile. Please try again.")
        }
      } else {
        const { error } = await supabase.from("profiles").insert({
          id: user.id,
          email: user.email ?? null,
          onboarding_completed_at: completedAt,
        })
        if (error) {
          console.error(error)
          toast.error("Failed to update profile. Please try again.")
        }
      }
    } catch {
      toast.error(t("onboarding.failedToSkip"))
    } finally {
      setSkipping(false)
    }
  }, [user, skipping, t])

  return { skipOnboarding, skipping }
}
